/**
 * @fileoverview Getting Started Page - Course Introduction Module
 * @module GettingStartedPage
 * 
 * Business Context:
 * - Introduces users to the Appeal Your Home course
 * - Two videos: Welcome to the Course + Texas Property Tax Appeal Timeline
 * - Shown before the 4-step journey begins
 * - Matches the UI/UX pattern of other step pages (Step2DocumentPage, etc.)
 * 
 * Layout:
 * - LEFT: Video navigation list
 * - RIGHT: Video player with info below
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import VideoPlayer from '../components/course/VideoPlayer';
import {
  ArrowLeft,
  Play,
  CheckCircle2,
  Clock,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  BookOpen
} from 'lucide-react';

interface VideoContent {
  id: string;
  title: string;
  description: string;
  duration: number;
  videoUrl: string;
  sectionOrder: number;
}

const GettingStartedPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videos, setVideos] = useState<VideoContent[]>([]);
  const [activeVideo, setActiveVideo] = useState<VideoContent | null>(null);
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  const [moduleProgress, setModuleProgress] = useState(0);
  const [isVideosExpanded, setIsVideosExpanded] = useState(true);

  // Fetch module content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const moduleRes = await apiClient.courses.getModule('module-00', {});

        if (moduleRes.data.success && moduleRes.data.data.content) {
          const videoList: VideoContent[] = moduleRes.data.data.content
            .filter((c: any) => c.contentType === 'video')
            .map((content: any) => ({
              id: content.id,
              title: content.metadata?.title || 'Video',
              description: content.metadata?.description || '',
              duration: content.metadata?.duration || 0,
              videoUrl: content.contentUrl || '',
              sectionOrder: content.metadata?.sectionOrder || 0,
            }))
            .sort((a: VideoContent, b: VideoContent) => a.sectionOrder - b.sectionOrder);

          setVideos(videoList);
          setModuleProgress(moduleRes.data.data.progress || 0);

          // Track completed videos from progress
          if (moduleRes.data.data.completedContent) {
            setCompletedVideos(new Set(moduleRes.data.data.completedContent));
          }

          // Auto-select video from navigation state or first video
          const stateVideoId = (location.state as any)?.videoId;
          if (stateVideoId) {
            const targetVideo = videoList.find((v: VideoContent) => v.id === stateVideoId);
            if (targetVideo) {
              setActiveVideo(targetVideo);
            } else {
              setActiveVideo(videoList[0]);
            }
          } else {
            // Find first unwatched video, or first video
            const unwatched = videoList.find((v: VideoContent) => !completedVideos.has(v.id));
            setActiveVideo(unwatched || videoList[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching intro module:', err);
        setError('Failed to load course content');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchContent();
    }
  }, [user, location.state]);

  // Handle video completion
  const handleVideoComplete = async () => {
    if (!activeVideo) return;

    try {
      // Update local state first
      const newCompleted = new Set([...completedVideos, activeVideo.id]);
      setCompletedVideos(newCompleted);
      
      // Calculate new progress percentage
      const newProgress = Math.round((newCompleted.size / videos.length) * 100);
      setModuleProgress(newProgress);
      
      // Save progress to backend
      await apiClient.courses.updateProgress('module-00', {
        progress: newProgress,
        lastContentId: activeVideo.id,
        completedContentIds: Array.from(newCompleted),
      });
      
      // Complete module if all videos are done
      if (newProgress >= 100) {
        await apiClient.courses.completeModule('module-00');
      }
      
      // Auto-advance to next video if available
      const currentIndex = videos.findIndex(v => v.id === activeVideo.id);
      if (currentIndex < videos.length - 1) {
        setTimeout(() => {
          setActiveVideo(videos[currentIndex + 1]);
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving video progress:', err);
    }
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if all videos are complete
  const allComplete = videos.length > 0 && videos.every(v => completedVideos.has(v.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading course content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Content</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>

            {/* Progress */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-teal-600">{completedVideos.size}/{videos.length}</span> videos complete
              </div>
              <div className="w-32 h-2 bg-slate-200 rounded-full">
                <div 
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${moduleProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Content - Left Navigation, Right Video */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT SIDE - Navigation */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden sticky top-24">
              {/* Module Header */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="pl-6">
                  <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-0.5">Introduction</p>
                  <h2 className="text-base font-bold text-slate-800 leading-tight">Getting Started</h2>
                </div>
              </div>

              {/* Video Navigation Section */}
              <div className="border-b border-slate-100">
                {/* Collapsible Header */}
                <button
                  onClick={() => setIsVideosExpanded(!isVideosExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-teal-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Course Introduction</h3>
                    <span className="text-xs text-slate-400 ml-1">
                      ({completedVideos.size}/{videos.length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      isVideosExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {/* Collapsible Content */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isVideosExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-4 pb-4 space-y-2">
                    {videos.map((video, index) => {
                      const isActive = video.id === activeVideo?.id;
                      const isCompleted = completedVideos.has(video.id);

                      return (
                        <button
                          key={video.id}
                          onClick={() => {
                            setActiveVideo(video);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-teal-50 border-2 border-teal-300'
                              : isCompleted
                              ? 'bg-emerald-50 border-2 border-emerald-200'
                              : 'bg-slate-50 border-2 border-slate-100 hover:border-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isCompleted ? 'bg-emerald-200' : isActive ? 'bg-teal-200' : 'bg-slate-200'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                              ) : isActive ? (
                                <Play className="w-4 h-4 text-teal-700" />
                              ) : (
                                <span className="text-slate-600 font-bold text-sm">{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-medium text-sm leading-tight ${
                                isActive ? 'text-teal-900' : isCompleted ? 'text-emerald-800' : 'text-slate-900'
                              }`}>
                                {video.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                <Clock className="w-3 h-3" />
                                <span>{formatDuration(video.duration)}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Next Step CTA */}
              <div className="p-4 bg-gradient-to-b from-slate-50 to-white">
                {allComplete ? (
                  <button
                    onClick={() => navigate('/step/1/document')}
                    className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    Continue to Step 1
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="text-center text-sm text-slate-500">
                    Watch both videos to continue
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Video Player */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              {activeVideo ? (
                <>
                  <VideoPlayer
                    key={activeVideo.id}
                    videoUrl={activeVideo.videoUrl}
                    onVideoComplete={handleVideoComplete}
                    currentProgress={0}
                    onProgress={() => {}}
                  />
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                          {activeVideo.title}
                        </h3>
                        <p className="text-slate-600">{activeVideo.description}</p>
                      </div>
                      {completedVideos.has(activeVideo.id) && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          Watched
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="aspect-video bg-slate-100 flex items-center justify-center">
                  <p className="text-slate-500">Select a video to start learning</p>
                </div>
              )}
            </div>

            {/* Completion CTA - shown below video when all complete */}
            {allComplete && (
              <div className="mt-6 p-5 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border-2 border-teal-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-slate-900 mb-1">You're Ready to Begin!</h4>
                    <p className="text-slate-600 text-sm mb-4">
                      Now that you understand the process, let's start documenting your home's issues.
                    </p>
                    <button
                      onClick={() => navigate('/step/1/document')}
                      className="px-5 py-2.5 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                    >
                      Start Step 1: Document Your Home
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Navigation */}
            <div className="mt-6 flex justify-between">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>

              {/* Right CTA: Next Video when available, otherwise Continue to Step 1 on last video */}
              {activeVideo && (() => {
                const currentIndex = videos.findIndex(v => v.id === activeVideo.id);
                const hasNextVideo = currentIndex < videos.length - 1;
                
                if (hasNextVideo) {
                  return (
                    <button
                      onClick={() => {
                        setActiveVideo(videos[currentIndex + 1]);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                      Next Video
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  );
                } else {
                  // On the last video — always show Continue to Step 1
                  return (
                    <button
                      onClick={() => navigate('/step/1/document')}
                      className="px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                      Continue to Step 1
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPage;
